import { Grid, Link, Radio, Typography, useMediaQuery, Stack } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, Fragment } from 'react';
import clsx from 'clsx';
import { formatMoney } from '../../../helpers';
import { ICategory, IOption, ISelectedSpecOption, ISpec } from '../../../models';
import { Loader } from '../../../components';

interface ISpecColorSchemeOptions {
  selectedOptions: ISelectedSpecOption[];
  category: ICategory;
  setModalImageSrc: (val: string) => void;
  setSelectedOptions: (val: ISelectedSpecOption[]) => void;
  fetchStoneColors: (val: ICategory) => void;
  hasCedarUpgrade: boolean;
  spec: ISpec;
  categoryIsLoading?: boolean;
  disabled: boolean;
}

export const SpecColorSchemeOptions: FC<ISpecColorSchemeOptions> = ({
  category,
  selectedOptions,
  setModalImageSrc,
  setSelectedOptions,
  fetchStoneColors,
  hasCedarUpgrade,
  spec,
  categoryIsLoading,
  disabled
}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const isSmMobile = useMediaQuery('(max-width: 600px');

  const setOptionsGridClass = (item: string) => {
    if (item === 'Siding Colors') {
      return clsx(classes.gridTable, classes.gridOptions);
    }
    return undefined;
  };
  const calculateColumnWidth = (count: number) => {
    const columnWidth = 100 / count;
    return `${columnWidth}%`;
  };
  const defaultHeader = {
    category: 'Color Scheme Options',
    enableQuantityInput: false,
    enabled: true,
    files: [],
    imageUrl: null,
    images: null,
    isContractOption: false,
    isHeader: true,
    price: 0,
    pricingType: 'LS'
  };

  const handleChange = (option: IOption, selectedIndex: number) => {
    let copy = JSON.parse(JSON.stringify(selectedOptions));
    if (selectedIndex > -1) {
      copy[selectedIndex] = {
        ...copy[selectedIndex],
        name: option.name,
        optionId: option.optionId,
        price: option.price,
        quantity: 1,
        imageUrl: option?.imageUrl ?? option?.images?.[0]?.imageUrl
      };
      setSelectedOptions(
        // remove duplicates
        copy.filter(
          (value, index, self) => index === self.findIndex(t => t.optionId === value.optionId)
        )
      );
    } else {
      // If option is not in selectedOptions, append array with the new selected option
      const newOption = {
        ...option,
        name: option.name,
        optionId: option.optionId,
        price: option.price,
        quantity: 1,
        imageUrl: option?.imageUrl ?? option?.images?.[0]?.imageUrl
      };
      copy.push(newOption);
      setSelectedOptions(
        // remove duplicates
        [...copy, newOption].filter(
          (value, index, self) => index === self.findIndex(t => t.optionId === value.optionId)
        )
      );
    }
  };

  const generateSidingsTable = () => {
    // All data
    const sidings = category.subCategories.filter(cat => cat.name === 'Siding Colors');
    const sidingOptions = sidings[0].options;
    const newSidingOptions: IOption[] = [];
    const columnLabels = [''];

    // Remove first part of name (row name), and add second half to list of column labels
    const formatTableColName = (option: IOption) => {
      const newName = option.name.split(' - ')[1];
      const optionType = option.optionId.split('_')[1];
      const newOption = { ...option, name: newName, optionType: optionType };
      if (!columnLabels.includes(newName) && newName && newName !== 'None') {
        columnLabels.push(newName);
      }
      newSidingOptions.push(newOption);
      return newOption;
    };

    //Headers
    const lapHeader = {
      ...defaultHeader,
      subCategory: 'Siding Color Lap',
      name: 'Lap',
      optionId: 'lapHeader'
    };
    const bnbHeader = {
      ...defaultHeader,
      subCategory: 'Siding Color B and B',
      name: 'Board and Batten',
      optionId: 'bnbHeader'
    };
    const shakeHeader = {
      ...defaultHeader,
      subCategory: 'Siding Color Shake',
      name: 'Shake',
      optionId: 'shakeHeader'
    };

    // Format / Gather Data
    newSidingOptions.push(lapHeader);
    sidingOptions
      .filter(option => option.name.includes('Lap'))
      .forEach(option => formatTableColName(option));
    newSidingOptions.push(bnbHeader);
    sidingOptions
      .filter(option => option.name.includes('B And B'))
      .forEach(option => formatTableColName(option));
    newSidingOptions.push(shakeHeader);
    sidingOptions
      .filter(option => option.name.includes('Shake'))
      .forEach(option => formatTableColName(option));

    // Calculate column widths
    const colWidth = calculateColumnWidth(columnLabels.length + 1); // Total up footer headers + None column addition

    return (
      <>
        {newSidingOptions.map((option, subcategoryOptionIndex) => {
          const currentSelectedOption = selectedOptions.find(o => o.optionId === option.optionId);
          return (
            <Fragment key={`${subcategoryOptionIndex}`}>
              <Grid
                className={clsx(
                  classes.gridTableCol,
                  classes.gridOptionCol,
                  option.isHeader ? classes.gridTableHeaderCol : undefined,
                  option.name === 'None' ? classes.gridTableNoneCol : undefined
                )}
                item
                container
                key={option.optionId}
                direction="row"
                spacing={1}
                alignItems="center"
                wrap="nowrap"
                sx={{
                  marginBottom: '.5rem',
                  flex: !isSmMobile ? `0 0 ${colWidth}` : undefined,
                  maxWidth: !isSmMobile ? colWidth : undefined,
                  marginLeft: !isSmMobile ? 0 : undefined,
                  marginRight: !isSmMobile ? 0 : undefined
                }}
              >
                {!option.isHeader && (
                  <>
                    <Grid item className={clsx('imgCol', classes.gridOptionInnerCol)}>
                      {option.images && option.images[0].imageUrl ? (
                        <picture
                          className={classes.picture}
                          onClick={() => setModalImageSrc(option.images[0].imageUrl)}
                        >
                          <img
                            src={option.images[0].imageUrl}
                            alt={option.images[0].text}
                            className={classes.optionImage}
                          />
                        </picture>
                      ) : (
                        <div className={clsx('empty', classes.optionImage)} />
                      )}
                    </Grid>
                    <Grid item className={clsx('inputCol', classes.gridOptionInnerCol)}>
                      <Radio
                        checked={!!currentSelectedOption}
                        disabled={!option.enabled || disabled}
                        onChange={() => {
                          let copy = JSON.parse(JSON.stringify(selectedOptions));

                          const correctSelectedIndex = copy.findIndex(
                            o => o.optionType && o.optionType === option.optionType
                          );

                          handleChange(option, correctSelectedIndex);
                        }}
                        value={option.optionId}
                        name={option.optionType}
                        inputProps={{ 'aria-label': option.name }}
                      />
                    </Grid>
                  </>
                )}
                <Grid item className={clsx('titleCol', classes.gridOptionInnerCol)}>
                  <Typography>
                    <span className="subCategoryName">{option.optionType} - </span>
                    {option.name}
                  </Typography>
                </Grid>
              </Grid>
            </Fragment>
          );
        })}
        {columnLabels.map((column, index) => {
          // Render Footer Headings
          return (
            <Fragment key={`${index}`}>
              <Grid
                className={clsx(
                  classes.gridTableCol,
                  classes.gridOptionCol,
                  classes.gridTableHeaderCol,
                  classes.gridTableFooterHeaderCol
                )}
                item
                container
                direction="row"
                spacing={1}
                alignItems="center"
                wrap="nowrap"
                sx={{
                  marginBottom: '.5rem',
                  flex: !isSmMobile ? `0 0 ${colWidth}` : undefined,
                  maxWidth: !isSmMobile ? colWidth : undefined,
                  marginLeft: !isSmMobile ? 0 : undefined,
                  marginRight: !isSmMobile ? 0 : undefined
                }}
              >
                <Grid item className={clsx('titleCol', classes.gridOptionInnerCol)}>
                  <Typography>{column}</Typography>
                </Grid>
              </Grid>
            </Fragment>
          );
        })}
      </>
    );
  };

  return (
    <>
      {category?.subCategories?.map(subCategory => {
        // remove custom ones from available selected options
        const selectedCategoryOptions = selectedOptions.filter(
          o => o.subCategory === subCategory.name
        );
        const currentSelectedOptionsInCategory = selectedCategoryOptions.filter(
          //no longer filter out the custom options
          o => true
        );
        const showCategory = subCategory.name === 'Shutter Color' && hasCedarUpgrade ? false : true;
        const flatMissingSubcategories = [
          ...(spec?.missingContractCategories ?? []),
          ...(spec?.missingTenDayCategories ?? [])
        ]?.flatMap(cat => cat.subCategories);
        const isInvalidSubCategory =
          subCategory.name === 'Siding Colors'
            ? flatMissingSubcategories?.filter(subCat => subCat.includes('Siding Color '))?.length >
              0
            : flatMissingSubcategories?.filter(subCat => subCat === subCategory.name)?.length > 0;

        if (!showCategory) {
          return null;
        }

        if (subCategory.name === 'Stone Color' && categoryIsLoading) {
          return (
            <Grid item container key={subCategory.name}>
              <Stack>
                <Typography variant="body1" className={classes.bold}>
                  {subCategory.name}
                </Typography>
                <Loader type="inline" position="centered" />
              </Stack>
            </Grid>
          );
        }
        return (
          <Grid item container key={subCategory.name}>
            {/** SUBCATEGORY NAME */}
            <Stack>
              <Typography variant="body1" className={classes.bold}>
                {subCategory.name}
              </Typography>
              {isInvalidSubCategory && (
                <Typography color="error" variant="caption" sx={{ display: 'block' }}>
                  Missing required options
                </Typography>
              )}
              {!!subCategory.note && (
                <Typography variant="body2" color="textSecondary">
                  {subCategory.note}
                </Typography>
              )}
            </Stack>
            {subCategory?.files && (
              <Grid item container flexDirection="column" marginBottom="1rem">
                {subCategory?.files?.map((file, index) => (
                  <Typography variant="body1" key={`${index}`} color="textSecondary">
                    <Link href={file?.fileUrl} target="_blank">
                      {file?.description}
                    </Link>
                  </Typography>
                ))}
              </Grid>
            )}
            <Grid item container className={setOptionsGridClass(subCategory.name)}>
              {/** SUBCATEGORY OPTIONS */}
              {subCategory.name === 'Siding Colors' && <>{generateSidingsTable()}</>}
              {subCategory.options.map((option, subcategoryOptionIndex) => {
                const currentSelectedOption = selectedOptions.find(
                  o => o.subCategory === subCategory.name && o.optionId === option.optionId
                );

                if (subCategory.name === 'Shutter Color') {
                  // Render in multi-column grid
                  return (
                    <Fragment key={`${subcategoryOptionIndex}`}>
                      <Stack alignItems="center">
                        <Radio
                          checked={!!currentSelectedOption}
                          disabled={!option.enabled}
                          onChange={() => {
                            let copy = JSON.parse(JSON.stringify(selectedOptions));
                            const correctSelectedIndex = copy.findIndex(
                              o => o.optionId === currentSelectedOptionsInCategory?.[0]?.optionId
                            );
                            handleChange(option, correctSelectedIndex);
                          }}
                          value={option.optionId}
                          name={option.name}
                          inputProps={{ 'aria-label': option.name }}
                        />
                        {option.images && option.images[0].imageUrl ? (
                          <picture
                            className={classes.picture}
                            onClick={() => setModalImageSrc(option.images[0].imageUrl)}
                          >
                            <img
                              src={option.images[0].imageUrl}
                              alt={option.images[0].text}
                              className={classes.optionImage}
                            />
                          </picture>
                        ) : (
                          <div className={clsx('empty', classes.optionImage)} />
                        )}
                        <Typography
                          variant="caption"
                          align="center"
                          sx={{
                            marginInline: '2px',
                            maxWidth: '4.25rem',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            display: 'block'
                          }}
                        >
                          {option.name}
                        </Typography>
                      </Stack>
                    </Fragment>
                  );
                } else if (subCategory.name !== 'Siding Colors') {
                  return (
                    <Fragment key={`${subcategoryOptionIndex}`}>
                      <Grid
                        item
                        container
                        key={option.optionId}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        wrap="nowrap"
                        sx={{ marginBottom: '.5rem' }}
                      >
                        <Grid item>
                          {option.images && option.images[0].imageUrl ? (
                            <picture
                              className={classes.picture}
                              onClick={() => setModalImageSrc(option.images[0].imageUrl)}
                            >
                              <img
                                src={option.images[0].imageUrl}
                                alt={option.images[0].text}
                                className={classes.optionImage}
                              />
                            </picture>
                          ) : (
                            <div className={classes.optionImage} />
                          )}
                        </Grid>
                        <Grid item>
                          <Radio
                            checked={!!currentSelectedOption}
                            disabled={!option.enabled}
                            onChange={() => {
                              let copy = JSON.parse(JSON.stringify(selectedOptions));
                              const correctSelectedIndex = copy.findIndex(
                                o => o.optionId === currentSelectedOptionsInCategory?.[0]?.optionId
                              );
                              handleChange(option, correctSelectedIndex);
                            }}
                            value={option.optionId}
                            name={option.name}
                            inputProps={{ 'aria-label': option.name }}
                          />
                        </Grid>
                        <Grid item>
                          <Typography>{option.name}</Typography>
                        </Grid>
                        {option?.files?.map(file => (
                          <Grid item>
                            <Link href={file?.fileUrl} target="_blank">
                              {file?.description}
                            </Link>
                          </Grid>
                        ))}
                        <Grid
                          item
                          marginLeft="auto"
                          className={isMobile ? classes.column : classes.row}
                        >
                          <Typography>{formatMoney(option.price)}</Typography>
                        </Grid>
                      </Grid>
                    </Fragment>
                  );
                } else {
                  return null;
                }
              })}
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  bold: {
    fontWeight: 600
  },
  optionImage: {
    cursor: 'pointer',
    height: 50,
    width: 50
  },
  picture: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& :not(:last-child)': {
      marginRight: theme.spacing(0.33)
    }
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    '& :not(:last-child)': {
      marginBottom: theme.spacing(0.33)
    }
  },
  numberInput: {
    width: '9ch',
    marginLeft: theme.spacing(1.5) + ' !important'
  },
  padLeft: {
    paddingLeft: theme.spacing(1)
  },
  colorImage: {
    width: 50,
    height: 50
  },
  pictureImages: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  pictureSwatchImages: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      color: theme.palette.common.black,
      fontWeight: 'bold',
      content: '"See Larger"',
      fontSize: '.8rem',
      position: 'absolute',
      textAlign: 'center',
      padding: 8,
      '@media (min-width: 480px)': {
        width: 200,
        height: 30,
        padding: 0
      },

      '@media (min-width: 1340px)': {
        width: 50,
        height: 80,
        padding: 8
      },
      '@media (min-width: 1410px)': {
        width: 100,
        height: 50
      }
    }
  },
  imagesWrapper: {
    flexWrap: 'wrap',
    '@media (min-width: 1340px)': {
      flexWrap: 'nowrap'
    }
  },
  categoryNotes: {
    '& a': {
      color: theme.palette.primary.main
    }
  },
  gridOptions: {
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      display: 'flex'
    }
  },

  gridOptionCol: {
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'column',
      flexWrap: 'wrap',
      display: 'flex'
    }
  },
  gridOptionInnerCol: {
    flexWrap: 'wrap',
    [theme.breakpoints.up('sm')]: {
      margin: 0,

      '&&.MuiGrid-root': {
        padding: theme.spacing(0.25, 1)
      },
      '&& .empty': {
        display: 'none'
      }
    },
    '&.imgCol': {
      [theme.breakpoints.up('sm')]: {
        order: 1
      }
    },
    '&.inputCol': {
      [theme.breakpoints.up('sm')]: {
        order: 0
      }
    },
    '&.titleCol': {
      [theme.breakpoints.up('sm')]: {
        order: 2,
        textAlign: 'center',
        '&& .MuiTypography-root': {
          fontSize: '0.85rem'
        }
      }
    }
  },
  gridTable: {
    [theme.breakpoints.up('sm')]: {
      flexWrap: 'wrap'
    }
  },
  gridTableCol: {
    [theme.breakpoints.up('sm')]: {
      '&& .titleCol': {
        display: 'none'
      },
      marginTop: theme.spacing(0.5),
      '&& .subCategoryName': {
        display: 'none'
      }
    }
  },
  gridTableHeaderCol: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'flex-start',
      '&& .titleCol': {
        display: 'block',
        textAlign: 'left'
      }
    }
  },
  gridTableNoneCol: {
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up('sm')]: {
      marginBottom: 0,
      '&& .titleCol': {
        display: 'block'
      }
    }
  },
  gridTableFooterHeaderCol: {
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'center',
      alignItems: 'center',
      '&& .titleCol': {
        display: 'block',
        textAlign: 'center'
      }
    }
  }
}));
